import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTimer } from '../contexts/TimerContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import {
  Bot, Send, Sparkles, Clock, Calendar, TrendingUp, RefreshCw, Trash2,
  Loader2, Sun, Zap, Target, ChevronRight, AlertTriangle, FileText,
  CheckSquare, UserCheck, XCircle, TrendingDown, FileX, Play, Square,
  Timer, Mail, ArrowRight, ChevronDown, ChevronUp, PanelRightClose,
  PanelRightOpen, Mic, MicOff
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

const CARD_ICONS = {
  'clock': Clock, 'alert-triangle': AlertTriangle, 'file-text': FileText,
  'x-circle': XCircle, 'calendar': Calendar, 'check-square': CheckSquare,
  'user-check': UserCheck, 'trending-down': TrendingDown, 'file-x': FileX,
};

const CARD_COLORS = {
  alert: { bg: 'bg-red-50 dark:bg-red-900/10', border: 'border-red-200 dark:border-red-800', icon: 'text-red-500' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-900/10', border: 'border-amber-200 dark:border-amber-800', icon: 'text-amber-500' },
  reminder: { bg: 'bg-blue-50 dark:bg-blue-900/10', border: 'border-blue-200 dark:border-blue-800', icon: 'text-blue-500' },
  info: { bg: 'bg-slate-50 dark:bg-slate-800/50', border: 'border-slate-200 dark:border-slate-700', icon: 'text-slate-500' },
  action: { bg: 'bg-violet-50 dark:bg-violet-900/10', border: 'border-violet-200 dark:border-violet-800', icon: 'text-violet-500' },
};

// Route map for clickable links
const ROUTE_MAP = {
  '/dashboard': 'Dashboard', '/tracker': 'Time Tracker', '/ai-assistant': 'AI Assistant',
  '/projects': 'Projects', '/admin/people': 'People', '/timesheets': 'Timesheets',
  '/time-off': 'Time Off', '/team-screenshots': 'Team Screenshots', '/team-locations': 'Team Locations',
  '/usage': 'Usage Dashboard', '/reports': 'Reports', '/invoices': 'Invoices', '/settings': 'Settings',
  '/admin/usage-categories': 'Usage Categories',
};

// Parse routes in text and make them clickable
const renderTextWithLinks = (text) => {
  const routePattern = /(\/[a-z][a-z0-9-/]*)/g;
  const parts = text.split(routePattern);
  return parts.map((part, i) => {
    if (ROUTE_MAP[part]) {
      return (
        <Link key={i} to={part} className="inline-flex items-center gap-0.5 text-violet-600 dark:text-violet-400 hover:underline font-medium">
          {ROUTE_MAP[part]}
          <ArrowRight className="w-3 h-3" />
        </Link>
      );
    }
    return part;
  });
};

// Custom markdown renderer with route link detection
const MarkdownMessage = ({ content }) => (
  <ReactMarkdown
    components={{
      p: ({ children }) => <p className="mb-1.5 last:mb-0 leading-relaxed">{children}</p>,
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      ul: ({ children }) => <ul className="list-disc ml-4 mb-1.5 space-y-0.5">{children}</ul>,
      ol: ({ children }) => <ol className="list-decimal ml-4 mb-1.5 space-y-0.5">{children}</ol>,
      li: ({ children }) => <li className="leading-relaxed">{children}</li>,
      code: ({ children }) => <code className="bg-slate-200 dark:bg-slate-700 px-1 py-0.5 rounded text-xs">{children}</code>,
      a: ({ href, children }) => {
        if (href && ROUTE_MAP[href]) {
          return <Link to={href} className="text-violet-600 dark:text-violet-400 hover:underline font-medium">{children}</Link>;
        }
        return <a href={href} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline">{children}</a>;
      },
    }}
  >
    {/* Replace route paths with markdown links for rendering */}
    {content.replace(/(\/(?:dashboard|tracker|ai-assistant|projects|admin\/people|timesheets|time-off|team-screenshots|team-locations|usage|reports|invoices|settings|admin\/usage-categories))/g, (match) => {
      return ROUTE_MAP[match] ? `[${ROUTE_MAP[match]}](${match})` : match;
    })}
  </ReactMarkdown>
);

// Voice input hook
const useVoiceInput = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let text = '';
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      setTranscript(text);
    };

    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognitionRef.current = recognition;
    return () => { try { recognition.stop(); } catch {} };
  }, []);

  const toggle = useCallback(() => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in this browser');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  const supported = !!((typeof window !== 'undefined') && (window.SpeechRecognition || window.webkitSpeechRecognition));

  return { isListening, transcript, toggle, supported };
};

const AIAssistant = () => {
  const { api, user } = useAuth();
  const { refreshTimer } = useTimer();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const voice = useVoiceInput();

  // Redirect employees — AI is for admin/manager only
  useEffect(() => {
    if (user && user.role === 'employee') navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [insights, setInsights] = useState(null);
  const [actionCards, setActionCards] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem(`ai_session_${user?.id || 'default'}`);
    if (saved) return saved;
    const newId = `session_${Date.now()}`;
    localStorage.setItem(`ai_session_${user?.id || 'default'}`, newId);
    return newId;
  });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load chat history from DB
  useEffect(() => {
    if (!api || historyLoaded) return;
    const loadHistory = async () => {
      try {
        const r = await api.get(`/ai/history?sessionId=${sessionId}`);
        if (r.data.messages?.length > 0) {
          setMessages(r.data.messages.map(m => ({
            role: m.role,
            content: m.content,
            timerAction: m.timer_action || null
          })));
        }
      } catch {}
      setHistoryLoaded(true);
    };
    loadHistory();
  }, [api, sessionId, historyLoaded]);

  // Sync voice transcript to input
  useEffect(() => {
    if (voice.transcript) setInput(voice.transcript);
  }, [voice.transcript]);

  useEffect(() => { fetchBriefing(); fetchInsights(); fetchActionCards(); }, [api]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchBriefing = async () => {
    if (!api) return;
    try {
      const response = await api.get('/ai/briefing');
      setBriefing(response.data);
      // Only set welcome message if no history was loaded
      if (messages.length === 0 && historyLoaded) {
        setMessages([{
          role: 'assistant',
          content: `${response.data.greeting}\n\n${response.data.tip}\n\nI can help with productivity insights, team reports, and time tracking analysis. Ask me anything!`
        }]);
      }
    } catch (error) { console.error('Failed to fetch briefing:', error); }
  };

  // Set welcome message after history check
  useEffect(() => {
    if (historyLoaded && messages.length === 0 && briefing) {
      setMessages([{
        role: 'assistant',
        content: `${briefing.greeting}\n\n${briefing.tip}\n\nI can help with productivity insights, team reports, and time tracking analysis. Ask me anything!`
      }]);
    }
  }, [historyLoaded, briefing]);

  const fetchInsights = async () => { if (!api) return; try { const r = await api.get('/ai/insights'); setInsights(r.data); } catch {} };
  const fetchActionCards = async () => { if (!api) return; try { const r = await api.get('/ai/action-cards'); setActionCards(r.data.cards || []); setAnomalies(r.data.anomalies || []); } catch {} };

  const sendMessage = async (text = input) => {
    if (!text.trim() || loading) return;
    const userMessage = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setFollowUps([]);
    setLoading(true);
    try {
      const response = await api.post('/ai/chat', { message: text.trim(), sessionId });
      const data = response.data;
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.response, timerAction: data.timerAction || null
      }]);
      if (data.followUps?.length > 0) setFollowUps(data.followUps);
      if (data.timerAction?.success) { refreshTimer(); fetchActionCards(); fetchBriefing(); }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble responding right now. Please try again." }]);
      toast.error('Failed to get response');
    } finally { setLoading(false); inputRef.current?.focus(); }
  };

  const clearChat = async () => {
    try {
      await api.post('/ai/clear', { sessionId });
      localStorage.removeItem(`ai_session_${user?.id || 'default'}`);
      setMessages([]); setFollowUps([]); fetchBriefing(); toast.success('Chat cleared');
    } catch { toast.error('Failed to clear chat'); }
  };

  const sendWeeklyDigest = async (teamWide = false) => {
    setSendingDigest(true);
    try {
      const endpoint = teamWide ? '/ai/send-team-digest' : '/ai/weekly-digest';
      const r = await api.post(endpoint); toast.success(r.data.message);
    } catch { toast.error('Failed to send digest'); }
    finally { setSendingDigest(false); }
  };

  const quickPrompts = [
    { icon: Clock, text: "What did I work on today?", color: "text-blue-500" },
    { icon: TrendingUp, text: "How was my week?", color: "text-purple-500" },
    { icon: Target, text: "What should I focus on?", color: "text-violet-500" },
    { icon: Calendar, text: "Team attendance this week?", color: "text-emerald-500" },
    { icon: FileText, text: "Pending approvals?", color: "text-amber-500" }
  ];

  const getTimerActionIcon = (action) => {
    if (!action) return null;
    if (action.type === 'start' && action.success) return <Play className="w-3.5 h-3.5 text-emerald-500" />;
    if (action.type === 'stop' && action.success) return <Square className="w-3.5 h-3.5 text-red-500" />;
    if (action.type === 'status') return <Timer className="w-3.5 h-3.5 text-blue-500" />;
    return null;
  };

  const totalAlerts = actionCards.length + anomalies.length;

  return (
    <Layout>
      <div className="h-[calc(100vh-8rem)] flex gap-4" data-testid="ai-assistant-page">
        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Compact Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
                <Bot className="w-4.5 h-4.5 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">AI Assistant</h2>
                <p className="text-[11px] text-slate-400">Ask anything about your team's productivity</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {totalAlerts > 0 && (
                <Button variant="outline" size="sm" onClick={() => setAlertsExpanded(!alertsExpanded)}
                  className={`h-8 rounded-lg text-xs gap-1.5 ${alertsExpanded ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-400' : ''}`}
                  data-testid="toggle-alerts-btn">
                  <AlertTriangle className="w-3.5 h-3.5" /> {totalAlerts}
                  {alertsExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { fetchBriefing(); fetchInsights(); fetchActionCards(); }} className="h-8 rounded-lg text-xs" data-testid="ai-refresh-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={clearChat} className="h-8 rounded-lg text-xs text-red-500 hover:text-red-600 hover:border-red-200" data-testid="ai-clear-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="h-8 rounded-lg text-xs hidden xl:flex" data-testid="toggle-sidebar-btn">
                {sidebarOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRightOpen className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Collapsible Alerts Strip */}
          {alertsExpanded && totalAlerts > 0 && (
            <div className="mb-3 flex gap-2 overflow-x-auto pb-1 custom-scrollbar" data-testid="action-cards-section">
              {actionCards.map(card => {
                const IconComp = CARD_ICONS[card.icon] || Clock;
                const colors = CARD_COLORS[card.type] || CARD_COLORS.info;
                return (
                  <div key={card.id} className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border ${colors.bg} ${colors.border} max-w-[280px]`} data-testid={`action-card-${card.id}`}>
                    <IconComp className={`w-4 h-4 flex-shrink-0 ${colors.icon}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{card.title}</p>
                      <p className="text-[10px] text-slate-500 truncate">{card.description}</p>
                    </div>
                    {card.action && (
                      <button onClick={() => navigate(card.action.route)} className="flex-shrink-0 text-slate-400 hover:text-slate-600" data-testid={`action-card-btn-${card.id}`}>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
              {anomalies.map(anomaly => {
                const IconComp = CARD_ICONS[anomaly.icon] || AlertTriangle;
                const isHigh = anomaly.severity === 'high';
                return (
                  <div key={anomaly.id} className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border ${isHigh ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800' : 'bg-amber-50 border-amber-200 dark:bg-amber-900/10 dark:border-amber-800'} max-w-[300px]`} data-testid={`anomaly-${anomaly.id}`}>
                    <IconComp className={`w-4 h-4 flex-shrink-0 ${isHigh ? 'text-red-500' : 'text-amber-500'}`} />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{anomaly.title}</p>
                      <p className="text-[10px] text-slate-500 truncate">{anomaly.description}</p>
                    </div>
                    {anomaly.affected > 0 && (
                      <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-medium ${isHigh ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                        {anomaly.affected}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Chat Container */}
          <div className="flex-1 premium-card p-4 overflow-hidden flex flex-col min-h-0">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-3 custom-scrollbar pr-1">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  }`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1.5 mb-1">
                        {msg.timerAction ? getTimerActionIcon(msg.timerAction) : <Sparkles className="w-3 h-3 text-violet-500" />}
                        <span className="text-[11px] font-medium text-violet-600 dark:text-violet-400">
                          {msg.timerAction ? 'Timer Action' : 'Inspiro AI'}
                        </span>
                      </div>
                    )}
                    <div className="text-sm">
                      {msg.role === 'assistant' ? (
                        <MarkdownMessage content={msg.content} />
                      ) : (
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                      <span className="text-sm text-slate-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Follow-up Suggestions */}
            {followUps.length > 0 && !loading && (
              <div className="pt-2 flex flex-wrap gap-1.5" data-testid="follow-up-suggestions">
                {followUps.map((fu, idx) => (
                  <button key={idx} onClick={() => sendMessage(fu)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/10 hover:bg-violet-100 dark:hover:bg-violet-900/20 transition-colors text-xs text-violet-700 dark:text-violet-400 font-medium"
                    data-testid={`follow-up-${idx}`}>
                    <ChevronRight className="w-3 h-3" />
                    {fu}
                  </button>
                ))}
              </div>
            )}

            {/* Quick Prompts - only on empty/welcome state */}
            {messages.length <= 1 && !loading && (
              <div className="pt-3 border-t border-slate-100 dark:border-slate-800 mt-3">
                <p className="text-[11px] text-slate-400 mb-1.5">Quick actions:</p>
                <div className="flex flex-wrap gap-1.5">
                  {quickPrompts.map((prompt, idx) => (
                    <button key={idx} onClick={() => sendMessage(prompt.text)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-xs text-slate-600 dark:text-slate-300"
                      data-testid={`quick-prompt-${idx}`}>
                      <prompt.icon className={`w-3 h-3 ${prompt.color}`} />
                      {prompt.text}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input with Voice */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 mt-3">
              <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                <div className="flex-1 relative">
                  <Input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
                    placeholder={voice.isListening ? "Listening..." : "Ask about team performance, reports, or anything..."}
                    className={`h-10 rounded-xl text-sm pr-10 ${voice.isListening ? 'ring-2 ring-red-400 border-red-300' : ''}`}
                    disabled={loading} data-testid="ai-chat-input" />
                  {voice.supported && (
                    <button type="button" onClick={voice.toggle}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors ${voice.isListening ? 'text-red-500 bg-red-50 dark:bg-red-900/20 animate-pulse' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      data-testid="voice-input-btn">
                      {voice.isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <Button type="submit" disabled={!input.trim() || loading}
                  className="h-10 px-4 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
                  data-testid="ai-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        {sidebarOpen && (
          <div className="w-72 flex-shrink-0 space-y-3 hidden xl:block overflow-y-auto custom-scrollbar">
            {briefing && (
              <div className="premium-card p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Sun className="w-3.5 h-3.5 text-amber-500" />
                  <span className="text-xs font-semibold text-slate-900 dark:text-white">Today</span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Time Tracked</span>
                    <span className="text-xs font-bold text-slate-900 dark:text-white timer-display">{briefing.today?.totalFormatted || '0h 0m'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-500">Entries</span>
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">{briefing.today?.entries || 0}</span>
                  </div>
                  {briefing.runningTimer && (
                    <div className="mt-1.5 p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-500/20">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 truncate">Timer: {briefing.runningTimer.project}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {insights && (
              <div className="premium-card p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-3.5 h-3.5 text-violet-500" />
                  <span className="text-xs font-semibold text-slate-900 dark:text-white">This Week</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-1.5 rounded-md bg-slate-50 dark:bg-slate-800">
                    <span className="text-[11px] text-slate-500">Total Time</span>
                    <span className="text-xs font-bold text-violet-600 dark:text-violet-400 timer-display">{insights.weekTotal}</span>
                  </div>
                  {insights.insights?.slice(0, 3).map((insight, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <ChevronRight className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] font-medium text-slate-700 dark:text-slate-300">{insight.title}: <span className="text-slate-900 dark:text-white">{insight.value}</span></p>
                        <p className="text-[10px] text-slate-500">{insight.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="premium-card p-3">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-semibold text-slate-900 dark:text-white">Weekly Digest</span>
              </div>
              <div className="space-y-1.5">
                <Button size="sm" variant="outline" className="w-full h-7 rounded-md text-[11px]" onClick={() => sendWeeklyDigest(false)} disabled={sendingDigest} data-testid="send-personal-digest-btn">
                  {sendingDigest ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />} Send My Digest
                </Button>
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <Button size="sm" variant="outline" className="w-full h-7 rounded-md text-[11px]" onClick={() => sendWeeklyDigest(true)} disabled={sendingDigest} data-testid="send-team-digest-btn">
                    {sendingDigest ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />} Send Team Digest
                  </Button>
                )}
              </div>
            </div>
            <div className="premium-card p-3 bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-500/10 dark:to-purple-500/10 border-violet-200 dark:border-violet-500/20">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-[11px] font-semibold text-violet-700 dark:text-violet-400">Pro Tip</span>
              </div>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                Ask about team performance, pending approvals, weekly comparisons, usage stats, or what to focus on!
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AIAssistant;
